import theme from "../theme";

import { useState, useRef } from "react";

import { useTranslation } from "react-i18next";

import Map from "../images/MAP2.webp";

import { ChevronDown, MapPin, ExternalLink } from "lucide-react";
import { address } from "framer-motion/client";

const PaperActivityCard = ({ title, description, iconColor, places }) => {
  const [open, setOpen] = useState(false);
  const contentRef = useRef(null);
  const { t } = useTranslation();

  const clipPathStyle = `polygon(
    0 0,
    10px 16.5%,
    0 33%,
    10px 49.5%,
    0 66%,
    10px 82.5%,
    0 100%,
    100% 100%,
    100% 0
  )`;

  return (
    <div className="w-full" onClick={() => setOpen((prev) => !prev)}>

      <div
        className="relative shadow-md"
        style={{
          backgroundColor: theme.pink,
          clipPath: clipPathStyle,
          paddingLeft: "4px", // This creates the border width
        }}
      >
        {/* Main content - positioned inside the border */}
        <div
          className="relative flex justify-start align-middle p-4 pl-6 shadow bg-white gap-4"
          style={{
            backgroundColor: theme.cream,
            clipPath: clipPathStyle,
          }}
        >
          {iconColor && (
            <div
              className="w-8 h-8 my-auto flex-none rounded-full"
              style={{ backgroundColor: iconColor }}
            />
          )}
          <h3 className="text-lg font-semibold my-auto flex-1 text-gray-900/80">
            {title}
          </h3>
          <div
            className="cursor-pointer hover:bg-white/30 p-2 rounded-full"
          >
            <ChevronDown
              className={`text-black/65 transition-transform duration-300 ${
                open ? "rotate-180" : ""
              }`}
            />
          </div>
        </div>
      </div>

      <div
        ref={contentRef}
        className="ml-10"
        style={{
          backgroundColor: theme.strongerPink + "22",
          maxHeight: open
            ? `${contentRef.current?.scrollHeight ?? 0}px`
            : "0px",
          opacity: open ? 1 : 0,
          overflow: "hidden",
          transition: "max-height 0.35s ease, opacity 0.35s ease",
        }}
      >
        {description && (
          <div className="flex p-3">
            <p className="text-sm text-black/50 pr-2">{description}</p>
          </div>
        )}
        {places && (
          <ul className="">
            {places.map((place, idx) => {
              const mapsLink = place.address
                ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.address)}`
                : null;

              return (
                <li
                  key={idx}
                  className="flex flex-row sm:items-center border-t-2 border-white/60 px-3 py-3"
                >
                  <div className="flex-1 pr-3">
                    <div className="text-sm font-medium text-black/80">
                      {t(place.title ?? place.name ?? "Untitled")}
                    </div>
                    {place.address && (
                      <div className="flex items-center text-xs text-black/50 gap-1 mt-0.5">
                        <MapPin className="w-3 h-3 flex-none" />
                        <span>{place.address}</span>
                      </div>
                    )}
                  </div>
                  {mapsLink && (
                    <a
                      href={mapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ backgroundColor: theme.sage }}
                      className="flex items-center gap-1 rounded-full text-white text-sm px-3 py-2 rounded hover:bg-white transition-colors"
                    >
                      <span>{t("btnOpenMaps")}</span>
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

// Activities Section
const ActivitiesSection = () => {
  const { t } = useTranslation();
  const activities = [
    {
      title: "actBeachesTitle",
      description: "actBeachesDesc",
      iconColor: "#008CFF",
      places: [
        {
          name: "actBeaches1Title",
          address: "17021 Alassio SV",
        },
        {
          name: "actBeaches2Title",
          address: "17031 Albenga SV",
        },
        {
          name: "actBeaches3Title",
          address: "17053 Laigueglia SV",
        },
      ],
    },
    {
        title: "actGolfTitle",
        description: "actGolfDesc",
        iconColor: "#FF00DD",
        places:[
            {
                title: "actGolf1Title",
                address: "Via del Golf, 7, 17033 Garlenda SV",
            }
        ]
    },
    {
      title: "actBoatRentalTitle",
      description: "actBoatRentalDesc",
      iconColor: "#FFA500", // CHANGE ON PIC TOO
      places: [
        {
          name: "actBoatRental1Title",
          address: "Porto Ferrari Luca, 4, 17021 Alassio SV",
        },
        {
          name: "actBoatRental2Title",
          address: "Via Aurelia, 41, 17051 Andora SV",
        },
      ],
    },
    {
      title: "actSnorkelingTitle",
      description: "actSnorkelingDesc",
      iconColor: "#20B2AA",
      places: [
        {
          name: "actSnorkeling1Title",
          address: "Porto Ferrari Luca, 4, 17021 Alassio SV",
        },
        {
          name: "actSnorkeling2Title",
          address: "Via Aurelia, 41, 17051 Andora SV",
        },
      ],
    },
    {
      title: "actCastleTitle",
      description: "actCastleDesc",
      iconColor: "#9E4F00",
      places: [
        {
          name: "actCastle1Title",
          address: "Via alla Marina, 2, 18010 Cervo IM",
        },
        {
          name: "actCastle2Title",
          address: "Via Armando Tornatore, 140, 17039 Zuccarello SV",
        },
      ],
    },
    {
      title: "actWineTastingTitle",
      description: "actWineTastingDesc",
      iconColor: "#6A00FF",
      places: [
        {
          name: "actWineTasting1Title",
          address: "Regione Vadino Superiore, 30, 17031 Albenga SV",
        },
        {
          name: "actWineTasting2Title",
          address: "17031 Reg. Marixe SV",
        },
      ],
    },
    {
      title: "actGrotteTitle",
      description: "actGrotteDesc",
      iconColor: "#FF0000",
      places: [
        {
            name: "actGrotte1Title",
            address: "Piazzale Dario Maineri, 1, 17055 Toirano SV"
        }
      ]
    },
  ];

  return (
    <section
      id="attivita"
      className="py-16 sm:py-20 lg:py-24 relative overflow-hidden"
      style={{ backgroundColor: theme.blush }}
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2
            className="text-4xl sm:text-5xl font-bold mb-6"
            style={{ color: theme.strongerPink }}
          >
            {t("headingActivities")}
          </h2>
          <p
            className="text-xl max-w-3xl mx-auto"
            style={{ color: theme.warmBrown }}
          >
            {t("activitiesSubheading")}
          </p>
        </div>
        <div className="flex flex-col lg:flex-row items-start gap-8">
          <img
            src={Map}
            className="rounded-xl w-full lg:w-1/2 max-w-xl mx-auto shadow-xl"
          />
          <div className="grid grid-cols-1 gap-3 flex-1 w-full max-w-xl mx-auto">
            {activities.map((activity, index) => (
              <PaperActivityCard
                key={index}
                {...activity}
                title={t(activity.title)}
                description={t(activity.description)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ActivitiesSection;
